import { useEffect, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { sessionService } from "redux-react-session";
import { useDispatch, useSelector } from "react-redux";
import {
  init,
  viewport,
  retrieveRawInitData,
  retrieveRawLaunchParams,
  miniApp,
  setMiniAppHeaderColor,
  miniAppHeaderColor,
} from "@telegram-apps/sdk-react";
import AllTransaction from "./AllTransaction";
import Wallet from "./Wallet";
import TransactionDetail from "./TransactionDetail";
import PaidPaymentPage from "../PaidPaymentPage";
import PrivateRoute from "../PrivateRoute";
import Settings from "../settings";
import AdvanceSettings from "../settings/AdvanceSettings";
import ChangeAccountName from "../settings/ChangeAccountName";
import CloseAccount from "../settings/CloseAccount";
import Verification from "../settings/Verification";
import RequestSubmitted from "../settings/RequestSubmitted";
import RequestInProcess from "../settings/RequestInProcess";
import RequestAccepted from "../settings/RequestAccepted";
import FiatCurrency from "../settings/FiatCurrency";
import Security from "../settings/Security";
import ChangePassword from "../settings/ChangePassword";
import OptionsLogin from "./OptionsLogin";
import OptionsForgotPassword from "./OptionsForgotPassword";
import {
  expireSession,
  getIsAuthenticated,
  isTMA,
  LS_FPJS_REQUEST_ID,
  LS_FPJS_VISITOR_ID,
  updateUserAndWalletAccountSession,
} from "../constants";
import OptionsForgotPasswordConfirmation from "./OptionsForgotPasswordConfirmation";
import OptionsCreateWalletConsent from "./OptionsCreateWalletConsent";
import {
  getMaintenanceStatus,
  setAccountAsset,
  setRedirectUrl,
  setShowBTCAmount,
} from "../../redux/actions";
import OptionsRegister from "./OptionsRegister";
import OptionsRegisterStepTwo from "./OptionsRegisterStepTwo";
import OptionsEmailVerification from "./OptionsEmailVerification";
import OptionsTwoFactorAuthentication from "./OptionsTwoFactorAuthentication";
import OptionsSetup2FA from "./OptionsSetup2FA";
import OptionsGetStarted from "./OptionsGetStarted";
import Profile from "./Profile";
import NewPasswordOptions from "./NewPasswordOptions";
import OptionsLoginStepTwo from "./OptionsLoginStepTwo";
import AccountStatus from "../settings/AccountStatus";
import AddCountryOptions from "./AddCountryOptions";
import UnderMaintenanceCommon from "../UnderMaintenanceCommon";
import BackdropLoader from "../backdropLoader/BackdropLoader";
import { loadingMsg } from "../messages";
import SocialMediaAuth from "../SocialMediaAuth";
import OptionsFooter from "./OptionsFooter";
import ApplyPromoCodeSettings from "../settings/ApplyPromoCodeSettings";
import AppliedPromoCode from "../settings/AppliedPromoCode";
import CommonSuccessPage from "../CommonSuccessPage";
import UnrecognizedDevice from "../unRecognizedDevice/UnrecognizedDevice";
import PhoneNumberVerification from "../verifyPhoneNumber/PhoneNumberVerification";
import MiniApps from "../miniApps";
import SearchMiniApps from "../miniApps/SearchMiniApps";
import MyAppsList from "../miniApps/myApps/MyAppsList";
import Contacts from "../settings/contacts";
import ContactDetails from "../settings/contacts/ContactDetails";
import Setup2FASettings from "../settings/mfa/Setup2FASettings";
import Limit from "../settings/limit/Limit";
import OptionsAccountSuspended from "./OptionsAccountSuspended";
import { useFPJSData } from "../../hooks/useFPJSData";

const showFooterIn = ["/wallet", "/history", "/settings", "/apps"];

function OptionsRouter() {
  const [maintenanceLoader, setMaintenanceLoader] = useState(true);
  const { setNewLocalstorageFPJSData } = useFPJSData();
  const [isDevice, setIsDevice] = useState("tdesktop");

  const redirectUrl = useSelector((state) => state.common.redirectUrl);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  if (isTMA()) {
    init();
    viewport.expand();
  }

  const handleFPJSData = async () => {
    const visitorId = localStorage.getItem(LS_FPJS_VISITOR_ID);
    const requestId = localStorage.getItem(LS_FPJS_REQUEST_ID);
    if (!visitorId || !requestId) {
      setNewLocalstorageFPJSData({});
    }
  };

  useEffect(() => {
    if (redirectUrl) {
      navigate(redirectUrl);
      if (redirectUrl) {
        setMaintenanceLoader(false);
        handleFPJSData();
      }
      dispatch(setRedirectUrl(""));
    }
  }, [redirectUrl]);

  useEffect(() => {
    handleFPJSData();
  }, []);

  if (isTMA() && miniApp?.mount() && setMiniAppHeaderColor?.isAvailable()) {
    setMiniAppHeaderColor?.("#ffffff");
    miniAppHeaderColor?.();
  }

  useEffect(() => {
    if (isTMA()) {
      const initialData = retrieveRawInitData();
      const urlParams = new URLSearchParams(initialData);
      const startParam = urlParams.get("start_param");
      const params = atob(startParam);
      const rawLaunchParams = retrieveRawLaunchParams();
      const rawParams = new URLSearchParams(rawLaunchParams);
      const platform = rawParams.get("tgWebAppPlatform");

      setIsDevice(platform?.toLowerCase());
      //navigate to verify email page in telegram mini app on verification email link
      if (params && location.search.includes("tgWebAppStartParam")) {
        navigate(`${params}`);
      }
    }
  }, []);

  const handleSession = async () => {
    try {
      const session = await sessionService.loadSession();
      const isAuthenticated = getIsAuthenticated(session);

      if (!isAuthenticated) {
        expireSession(false);
        setMaintenanceLoader(false);
        return;
      }

      // Refresh token once if needed before making API calls
      await updateUserAndWalletAccountSession();

      dispatch(setAccountAsset());
      dispatch(
        getMaintenanceStatus({ maintenanceLoader: setMaintenanceLoader })
      );

      if (session.showBtcAmount)
        dispatch(setShowBTCAmount(session.showBtcAmount));
    } catch (error) {
      setMaintenanceLoader(false);
    }
  };

  useEffect(() => {
    handleSession();
  }, []);

  return maintenanceLoader ? (
    <BackdropLoader
      open={maintenanceLoader}
      alt={loadingMsg}
      text={loadingMsg}
      customClass="loading-in-auth"
    />
  ) : (
    <>
      <Routes>
        <Route path="/" element={<OptionsGetStarted isDevice={isDevice} />} />
        <Route path="/login" element={<OptionsLogin />} />
        <Route path="/login-step-two" element={<OptionsLoginStepTwo />} />
        <Route path="/forgot-password" element={<OptionsForgotPassword />} />
        <Route
          path="/forgot-password-confirmation"
          element={<OptionsForgotPasswordConfirmation />}
        />
        <Route
          path="/create-wallet-consent"
          element={<OptionsCreateWalletConsent />}
        />
        <Route
          path="/account-suspended"
          element={
            <PrivateRoute>
              <OptionsAccountSuspended />
            </PrivateRoute>
          }
        />
        <Route path="/register" element={<OptionsRegister />} />
        <Route path="/register-step-two" element={<OptionsRegisterStepTwo />} />
        <Route path="/verify-email" element={<OptionsEmailVerification />} />
        <Route path="/new-password" element={<NewPasswordOptions />} />
        <Route
          path="/two-factor"
          element={<OptionsTwoFactorAuthentication />}
        />
        <Route path="/two-factor-setup" element={<OptionsSetup2FA />} />
        <Route path="/unrecognized-device" element={<UnrecognizedDevice />} />
        <Route
          path="/phone-number-verification"
          element={<PhoneNumberVerification />}
        />
        <Route path="/dashboard" element={<Navigate to="/wallet" replace />} />
        <Route
          path="/close-modal"
          element={<Navigate to="/wallet" replace />}
        />
        <Route exact path="/google-auth" element={<SocialMediaAuth />} />
        <Route exact path="/apple-auth" element={<SocialMediaAuth />} />
        <Route
          path="/add-country"
          element={
            <PrivateRoute>
              <AddCountryOptions />
            </PrivateRoute>
          }
        />
        <Route
          path="/wallet"
          element={
            <PrivateRoute>
              <Wallet isIosDevice={isDevice === "ios"} />
            </PrivateRoute>
          }
        />
        <Route
          path="/history"
          element={
            <PrivateRoute>
              <AllTransaction isShowHeader={true} />
            </PrivateRoute>
          }
        />
        <Route
          path="/history/:id"
          element={
            <PrivateRoute>
              <TransactionDetail />
            </PrivateRoute>
          }
        />

        <Route
          path="/pending-confirmations"
          element={
            <PrivateRoute>
              <AllTransaction />
            </PrivateRoute>
          }
        />
        <Route
          path="/pending-confirmations/:id"
          element={
            <PrivateRoute>
              <TransactionDetail />
            </PrivateRoute>
          }
        />
        <Route
          path="/apps/search"
          element={
            <PrivateRoute>
              <SearchMiniApps />
            </PrivateRoute>
          }
        />
        <Route
          path="/apps"
          element={
            <PrivateRoute>
              <MiniApps />
            </PrivateRoute>
          }
        />
        <Route
          path="/my-apps/list"
          element={
            <PrivateRoute>
              <MyAppsList />
            </PrivateRoute>
          }
        />

        <Route
          path="/settings"
          element={
            <PrivateRoute>
              <Settings />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings/advance-settings"
          element={
            <PrivateRoute>
              <AdvanceSettings />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings/advance-settings/change-account-name"
          element={
            <PrivateRoute>
              <ChangeAccountName />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings/advance-settings/permission"
          element={
            <PrivateRoute>
              <AccountStatus />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings/advance-settings/close-account"
          element={
            <PrivateRoute>
              <CloseAccount />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings/advance-settings/close-account/verification"
          element={
            <PrivateRoute>
              <Verification />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings/advance-settings/close-account/request-submitted"
          element={
            <PrivateRoute>
              <RequestSubmitted />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings/advance-settings/close-account/request-in-process"
          element={
            <PrivateRoute>
              <RequestInProcess />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings/advance-settings/close-account/request-accepted"
          element={
            <PrivateRoute>
              <RequestAccepted />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings/fiat-currency"
          element={
            <PrivateRoute>
              <FiatCurrency />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings/security"
          element={
            <PrivateRoute>
              <Security />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings/two-step-verification"
          element={
            <PrivateRoute>
              <Setup2FASettings isIosDevice={isDevice === "ios"} />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings/security/change-password"
          element={
            <PrivateRoute>
              <ChangePassword />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings/apply-promo-code"
          element={
            <PrivateRoute>
              <ApplyPromoCodeSettings />
            </PrivateRoute>
          }
        />
        <Route
          path="/promo-code-success"
          element={
            <PrivateRoute>
              <AppliedPromoCode />
            </PrivateRoute>
          }
        />
        <Route
          path="/success-page"
          element={
            <PrivateRoute>
              <PaidPaymentPage isFromBrowser={true} />
            </PrivateRoute>
          }
        />
        <Route
          path="/success"
          element={
            <PrivateRoute>
              <CommonSuccessPage isFromBrowser={true} />
            </PrivateRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <PrivateRoute>
              <Profile />
            </PrivateRoute>
          }
        />
        <Route
          path="/under-maintenance"
          element={
            <PrivateRoute>
              <UnderMaintenanceCommon isFromBrowser={true} />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings/limits"
          element={
            <PrivateRoute>
              <Limit />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings/contacts"
          element={
            <PrivateRoute>
              <Contacts />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings/contacts/:id"
          element={
            <PrivateRoute>
              <ContactDetails />
            </PrivateRoute>
          }
        />
      </Routes>
      {showFooterIn.includes(location.pathname) && <OptionsFooter />}
    </>
  );
}

export default OptionsRouter;
